import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {Box, Breakpoint, Stack, Theme, Typography, useMediaQuery, useTheme} from '@mui/material';
import {ChartData, ChartDataset, ChartOptions, FontSpec, TooltipItem} from 'chart.js';
import {TreemapChart} from '../../../../../../../../components/custom-chartjs-charts';
import {Align, Anchor, TextAlign} from "chartjs-plugin-datalabels/types/options";
import DashboardWidgetFooter from "../../dashboard-widget/footer";
import {SearchDataSettingsOrderBy, SearchDataSettingsPaginationInfo} from "../../../../../../../../hooks/use-search";
import {
    DashboardViewFilterSectionFilters,
    DashboardWidgetLayoutConfig,
    DashboardWidgetOrderByField
} from "../../../types";
import {GetMinHeightReturnValue} from "../../../dashboard-widget.config";
import {
    TreemapControllerDatasetLabelsOptions,
    TreemapControllerDatasetOptions,
    TreemapScriptableContext
} from "chartjs-chart-treemap";
import {AnyObject} from "chart.js/dist/types/basic";
import {useTranslation} from "react-i18next";

// Interfaces
export interface DataPoint {
    [key: string]: string | number;  // Allow any key-value pairs
}

export type FormatterScriptable<T, TContext> = T | ((ctx: TContext, options: AnyObject, sum: number) => T | undefined);


export interface TreemapLabelsConfig extends Omit<TreemapControllerDatasetLabelsOptions, 'formatter' | 'font'> {
    formatter?: FormatterScriptable<string | Array<string>, TreemapScriptableContext>;
    font?: FormatterScriptable<FontSpec | Array<FontSpec>, TreemapScriptableContext>;
}

export interface TreemapConfig extends Omit<TreemapControllerDatasetOptions<DataPoint>, 'data' | 'tree' | 'labels'> {
    displayMode: 'containerBoxes' | 'headerBoxes';
    labels?: TreemapLabelsConfig;
}

export interface TooltipConfig {
    key: string;           // The key in the data object to display
    label: string;         // Label to show in tooltip
    format?: (value: string | number) => string;  // Optional formatter
}

export interface FiltersConfig {
    enabled: boolean;
    onClick?: (key: string, point: DataPoint) => void;
}

export interface DataLabelConfig {
    enabled?: boolean;
    format?: (value: string | number) => string;
    color?: (value: string | number) => string;
    align?: Align;
    anchor?: Anchor;
    textAlign?: TextAlign;
    offset?: number;
}

export interface TreemapWidgetConfig {
    labelKey: string;                    // Key for x-axis values (used for grouping)
    labelFormatter?: (value: string) => string;  // Optional formatter for x-axis values
    tree: TreemapConfig;                 // Treemap configuration
    tooltipConfig: TooltipConfig[];     // Tooltip configuration
    filtersConfig?: FiltersConfig;      // Filters interaction configuration
    dataLabelConfig?: DataLabelConfig;  // Data label configuration
    sortByKey?: string;                  // Key to sort by
    sortBy?: (a: keyof DataPoint, b: keyof DataPoint) => number; // Sorting function
}

interface TreemapProps {
    data: DataPoint[];           // Array of data points
    config: TreemapWidgetConfig; // Chart configuration - now required
    filters?: DashboardViewFilterSectionFilters;
    widgetFilters?: DashboardViewFilterSectionFilters;
    minHeight: GetMinHeightReturnValue,
    isLoading: boolean;
    paginationInfo?: SearchDataSettingsPaginationInfo;
    setPaginationInfo: React.Dispatch<React.SetStateAction<SearchDataSettingsPaginationInfo | undefined>>;
    orderBy?: SearchDataSettingsOrderBy;
    setOrderBy?: React.Dispatch<React.SetStateAction<SearchDataSettingsOrderBy | undefined>>;
    underlyingDataVisibility?: DashboardWidgetLayoutConfig['underlyingDataVisibility'];
    orderByFields?: DashboardWidgetOrderByField[]; // Fields available for ordering
    hasPagination?: boolean;
    connectionId?: string;
}

/**
 * Parses a treemap cell value into a finite number.
 */
const toFiniteNumber = (value: unknown): number | undefined => {
    const numValue = typeof value === 'number'
        ? value
        : typeof value === 'string'
            ? Number.parseFloat(value)
            : Number.NaN;
    return Number.isFinite(numValue) ? numValue : undefined;
};

/**
 * Treemap squarify only accepts weights greater than 0. Zero and negative values produce NaN geometry.
 */
const isPositiveWeight = (value: unknown): boolean => {
    const numValue = toFiniteNumber(value);
    return numValue !== undefined && numValue > 0;
};

const formatValue = (value: string | number | null | undefined, format?: (value: string | number) => string) => {
    if (value === undefined) {
        return '';
    }
    if (value === null) {
        return format ? format(value as unknown as string | number) : '';
    }
    const numValue = toFiniteNumber(value);
    if (numValue === undefined) return '';
    return format ? format(numValue) : numValue.toLocaleString();
};

type TreemapRectPoint = { _data?: DataPoint };

export const useBreakpoint = (): Breakpoint => {
    const xsQuery = useMediaQuery<Theme>(theme => theme.breakpoints.only('xs'));
    const smQuery = useMediaQuery<Theme>(theme => theme.breakpoints.only('sm'));
    const mdQuery = useMediaQuery<Theme>(theme => theme.breakpoints.only('md'));
    const mdlgQuery = useMediaQuery<Theme>(theme => theme.breakpoints.only('mdlg'));
    const lgQuery = useMediaQuery<Theme>(theme => theme.breakpoints.only('lg'));
    const lgxlQuery = useMediaQuery<Theme>(theme => theme.breakpoints.only('lgxl'));
    const xlQuery = useMediaQuery<Theme>(theme => theme.breakpoints.only('xl'));
    const xxlQuery = useMediaQuery<Theme>(theme => theme.breakpoints.only('xxl'));

    if (xxlQuery) return 'xxl';
    if (xlQuery) return 'xl';
    if (lgxlQuery) return 'lgxl';
    if (lgQuery) return 'lg';
    if (mdlgQuery) return 'mdlg';
    if (mdQuery) return 'md';
    if (smQuery) return 'sm';
    if (xsQuery) return 'xs';
    return 'xs'; // Default to xs if no match
}

export const DashboardTreemapWidget: React.FC<TreemapProps> = ({
                                                                   data,
                                                                   config,
                                                                   minHeight,
                                                                   isLoading,
                                                                   paginationInfo,
                                                                   setPaginationInfo,
                                                                   orderBy,
                                                                   setOrderBy,
                                                                   underlyingDataVisibility,
                                                                   orderByFields = [],
                                                                   hasPagination,
                                                                   connectionId,
                                                               }) => {
    const treemapInstance = useRef<React.ElementRef<typeof TreemapChart>>(null);
    const {labelFormatter, filtersConfig: {onClick: filtersConfigOnClick} = {}, sortBy} = config;
    const breakpoint = useBreakpoint();
    const theme = useTheme();
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.dashboard-v2.treemap'});

    const sortedData = useMemo(() => {
        if (!!config.sortByKey && !!sortBy && (orderBy?.field === undefined || !orderBy?.field?.length)) {
            const sorted = [...data]
            sorted.sort((a, b) => sortBy(a[config.sortByKey!], b[config.sortByKey!]));
            return sorted;
        }
        return data;
    }, [config.sortByKey, data, orderBy?.field, sortBy]);

    /**
     * Rows whose size-key is a finite number greater than 0. Squarify cannot lay out the rest.
     */
    const plottableData = useMemo(() => {
        const sizeKey = config.tree.key;
        if (!sizeKey) return [];
        return sortedData.filter((item) => isPositiveWeight(item?.[sizeKey]));
    }, [config.tree.key, sortedData]);

    /**
     * Handle click events on the chart using the rectangle's source row, not the input index.
     */
    const handleClick = useCallback<Required<ChartOptions<'treemap'>>['onClick']>((_event, elements, chart) => {
        if (!elements.length || !config.filtersConfig?.enabled || !filtersConfigOnClick) {
            return;
        }
        const element = elements[0];
        const dataset = chart.data.datasets[element.datasetIndex];
        const point = (dataset?.data?.[element.index] as TreemapRectPoint | undefined)?._data;
        if (!point) {
            return;
        }
        filtersConfigOnClick(config.labelKey, point);
    }, [config.filtersConfig?.enabled, filtersConfigOnClick, config.labelKey]);

    const prevPageNumber = useRef(paginationInfo?.currentPage);

    /**
     * Store the previous page number when the component unmounts
     */
    useEffect(() => () => {
        prevPageNumber.current = paginationInfo?.currentPage;
    }, [paginationInfo?.currentPage]);

    /**
     * Sum of the size-key across plottable rows only, so label percentages match the laid-out area.
     */
    const sum = useMemo(() => {
        const sizeKey = config.tree.key;
        if (!sizeKey) return 0;
        return plottableData.reduce((acc, item) => {
            return acc + (toFiniteNumber(item?.[sizeKey]) ?? 0);
        }, 0);
    }, [config.tree.key, plottableData]);

    const dataMock = useMemo(() => [], []);

    /**
     * Slice `tree` on every rebuild so the 3.1.0 controller regenerates rectangles after
     * react-chartjs-2 assigns the empty `data` array onto the live dataset.
     */
    const chartData = useMemo<ChartData<'treemap'>>(() => ({
        datasets: [
            {
                ...config.tree,
                labels: {
                    ...(config.tree.labels ?? {}),
                    formatter: (context, options) => {
                        if (context?.raw == null || context.dataset?.data?.[context.dataIndex] == null) {
                            return undefined;
                        }
                        return typeof config.tree.labels?.formatter === 'function'
                            ? config.tree.labels?.formatter(context, options, sum)
                            : config.tree.labels?.formatter;
                    },
                    font: (context, options) => typeof config.tree.labels?.font === 'function'
                        ? config.tree.labels?.font(context, options, sum)
                        : config.tree.labels?.font,
                },
                groups: config.tree.groups?.map<string>(group => group.toString()),
                sumKeys: config.tree.sumKeys?.map<string>(key => key.toString()),
                key: config.tree.key?.toString() as string,
                treeLeafKey: config.tree.treeLeafKey?.toString(),
                data: dataMock,
                datalabels: {display: false},
                tree: plottableData.slice(),
            } satisfies ChartDataset<"treemap", DataPoint[]>,
        ]
            .filter(bar => underlyingDataVisibility?.[bar.key ?? ""] !== false)
    }), [config.tree, plottableData, sum, underlyingDataVisibility, dataMock]);

    const options = useMemo<ChartOptions<'treemap'>>(() => ({
        responsive: true,
        resizeDelay: 100,
        maintainAspectRatio: false,
        onClick: handleClick,
        onHover: (event, chartElement) => {
            if (chartElement && event?.native?.target && config.filtersConfig?.enabled) {
                (event.native.target as any).style?.setProperty?.('cursor', 'pointer');
            }
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    title: (context: TooltipItem<'treemap'>[]) => {
                        if (context.length === 0) return '';
                        const item = (context[0].dataset.data[context[0].dataIndex] as TreemapRectPoint | undefined)?._data;
                        if (!item) return '';
                        const value = item[config.labelKey]?.toString();
                        return labelFormatter ? labelFormatter(value) : value;
                    },
                    label: (context: TooltipItem<'treemap'>) => {
                        const item = (context.dataset.data[context.dataIndex] as TreemapRectPoint | undefined)?._data;
                        if (!item) return [];
                        return config.tooltipConfig
                            .map(tooltipItem => {
                                const rawValue = item[tooltipItem.key];
                                const formattedValue = formatValue(rawValue, tooltipItem.format);
                                return `${tooltipItem.label}: ${formattedValue}`;
                            });
                    }
                }
            },
        },
    }), [handleClick, config.filtersConfig?.enabled, config.labelKey, config.tooltipConfig, labelFormatter]);

    const treemapSize = useMemo(() => {
        const keys = theme.breakpoints.keys as Breakpoint[];
        // we want to normalize the minHeight so that for example, if xs and md are given, the minHeight has xs, sm = xs, md, lg = md, lgxl = lg, xl = xl, xxl = xxl
        const minHeightNormalized = keys.reduce((acc, key, index) => {
            // If this breakpoint has a defined value in minHeight, use it
            if (minHeight?.[key] !== undefined) {
                acc[key] = minHeight?.[key] as number;
            } else {
                // Otherwise, find the closest smaller breakpoint with a defined value
                let closestSmallerBreakpoint: Breakpoint | undefined;
                for (let i = index - 1; i >= 0; i--) {
                    if (minHeight?.[keys[i]] !== undefined) {
                        closestSmallerBreakpoint = keys[i];
                        break;
                    }
                }

                // If a smaller breakpoint with a defined value was found, use its value
                if (closestSmallerBreakpoint) {
                    acc[key] = minHeight?.[closestSmallerBreakpoint] as number;
                } else {
                    // If no smaller breakpoint has a defined value, use the value of the smallest breakpoint
                    // This is a fallback and should not happen if at least 'xs' has a defined value
                    const smallestBreakpoint = keys.find(k => minHeight?.[k] !== undefined);
                    acc[key] = smallestBreakpoint ? (minHeight?.[smallestBreakpoint] as number) : 300; // Default to 300 if no breakpoint has a defined value
                }
            }
            return acc;
        }, {} as Record<Breakpoint, number>);
        return minHeightNormalized[breakpoint];
    }, [breakpoint, minHeight, theme.breakpoints.keys]);

    return (
        <Stack direction={"column"} gap={0.5}>
            <Box sx={{width: '100%', minHeight: minHeight, height: treemapSize}}>
                {
                    plottableData.length > 0 &&
                    <TreemapChart
                        key={connectionId}
                        data={chartData}
                        options={options}
                        ref={treemapInstance}
                        height={treemapSize}
                    />
                }
                {
                    plottableData.length === 0 &&
                    <Stack
                        direction={"column"}
                        alignItems={"center"}
                        justifyContent={"center"}
                        sx={{width: '100%', height: '100%'}}
                    >
                        <Typography variant={'body2'} fontWeight={500} color={'text.secondary'} textAlign={'center'}>
                            {t(t => t.emptyState)}
                        </Typography>
                    </Stack>
                }
            </Box>
            <TreemapXAxis/>
            <DashboardWidgetFooter
                paginationInfo={paginationInfo}
                setPaginationInfo={setPaginationInfo}
                isLoading={isLoading}
                orderBy={orderBy}
                setOrderBy={setOrderBy}
                orderByFields={orderByFields}
                hasPagination={hasPagination}
                data={data}
            />
        </Stack>
    );
};

const TreemapXAxis = () => {
    const isSmall = useMediaQuery<Theme>((theme) => theme.breakpoints.only('xs'));

    return (
        <>
            <Box sx={{
                position: 'relative',
                width: '100%',
                height: '20px',
            }}>
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '1px',
                    backgroundColor: 'divider',
                }}/>
                <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    textAlign: 'center',
                    fontSize: '12px',
                    color: 'text.secondary',
                }}
                >
                    {Array.from({length: isSmall ? 5 : 10}, (_, i) => (
                        <Box component={'span'} key={i} sx={{
                            display: 'inline-block',
                            width: `${100 / ((isSmall ? 5 : 10))}%`,
                            textAlign: 'end',
                        }}>
                            {(i + 1) * (100 / (isSmall ? 5 : 10))}%
                        </Box>
                    ))}
                </Box>
            </Box>
        </>
    );
}

export default DashboardTreemapWidget;
